// alternative-dashboard/alternative-components/CFOCopilot/CustomeComposedChart.tsx
import { useEffect, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useCountry } from "@/app/services/context/CountryContext";
import { gxDashboardAccountsMonthlyCashflowApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import type {
  DashboardAccountsMonthlyCashflowPayload,
  DashboardAccountsMonthlyCashflowResponse,
} from "@/app/types/alterNativeDashboardLayout";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import {
  mapMonthlyCashflowToChartPoints,
  type MonthlyCashflowChartPoint,
} from "../../mappers/monthlyCashflowMapper";
import "./styles/CustomeChart.scss";
import NoDataFound from "@/app/components/common/warning/NoDataFound";

interface CustomeChartProps {
  title?: string;
}

const CASHFLOW_LOOKBACK_DAYS = 180;

const formatDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Builds a rolling [today - 180 days, today] range, recalculated on every call
// so the payload always reflects the current system date.
const buildRollingCashflowRange = () => {
  const endDate = new Date();

  const startDate = new Date();
  startDate.setDate(startDate.getDate() - CASHFLOW_LOOKBACK_DAYS);

  return { startDate, endDate };
};

const amountFormat = (amount: any) => {
  if (amount >= 1000000000) {
    return Math.floor(amount / 1000000000) + "B";
  } else if (amount >= 1000000) {
    return Math.floor(amount / 1000000) + "m";
  } else if (amount >= 1000) {
    return Math.floor(amount / 1000) + "k";
  } else if (amount <= -1000000000) {
    return Math.floor(amount / 1000000000) + "B";
  } else if (amount <= -1000000) {
    return Math.floor(amount / 1000000) + "m";
  } else if (amount <= -1000) {
    return Math.floor(amount / 1000) + "k";
  } else {
    return amount?.toString();
  }
};

const CustomeChart = ({ title }: CustomeChartProps) => {
  const { selectedCountry } = useCountry();
  const currencyCode = selectedCountry?.currency?.code ?? "";

  const [chartData, setChartData] = useState<MonthlyCashflowChartPoint[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!currencyCode) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const loadMonthlyCashflow = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const { startDate, endDate } = buildRollingCashflowRange();

        const payload: DashboardAccountsMonthlyCashflowPayload = {
          pageNo: 0,
          pageSize: 0,
          filterBy2List: [
            {
              filterKey: "CURRENCY_CODE",
              filterStringValue: currencyCode,
            },
            {
              filterKey: "START_DATE",
              filterStringValue: formatDate(startDate),
            },
            {
              filterKey: "END_DATE",
              filterStringValue: formatDate(endDate),
            },
          ],
        };

        const response = await gxDashboardAccountsMonthlyCashflowApi(payload);

        if (!isMounted) return;

        if (response?.status === 1) {
          setChartData(
            mapMonthlyCashflowToChartPoints(
              response.data as unknown as DashboardAccountsMonthlyCashflowResponse[],
            ),
          );
        } else {
          setChartData([]);
        }
      } catch (error) {
        if (isMounted) {
          setHasError(true);
          setChartData([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMonthlyCashflow();

    return () => {
      isMounted = false;
    };
  }, [currencyCode]);

  return (
    <div className="gx-custome-chart">
      <h1 className="gx-p18-b">{title || "Inflow vs outflow · 6 months"}</h1>

      <div className="gx-chart-inflow-outflow-legend-wrapper">
        <div className="gx-chart-inflow-outflow-legend">
          <div className="gx-chart-inflow-outflow-legend-bar-1"></div>
          <span className="gx-font-semibold gx-chart-inflow-outflow-legend-mame">
            InFlow
          </span>
        </div>
        <div className="gx-chart-inflow-outflow-legend">
          <div className="gx-chart-inflow-outflow-legend-bar-2"></div>
          <span className="gx-font-semibold gx-chart-inflow-outflow-legend-mame">
            OutFlow
          </span>
        </div>
      </div>

      <div className="relative" style={{ minHeight: 220 }}>
        {isLoading && (
          <div className="gx-bene-form-container-loading-overlay">
            <SubmitLoading />
          </div>
        )}

        {!isLoading && hasError && (
          <p className="gx-p14-r gx-error-text">
            Failed to load inflow vs outflow
          </p>
        )}

        {!isLoading && !hasError && chartData.length === 0 && (
          // <p className="gx-p14-r gx-text-gray">No cashflow data available</p>
          <NoDataFound message="No cashflow data available" />
        )}

        {!isLoading && !hasError && chartData.length > 0 && (
          <ComposedChart
            style={{ width: "100%", height: 200 }}
            margin={{ left: -25, bottom: -5, top: 20 }}
            responsive
            className="gx-font-medium"
            data={chartData}
            barGap={0}
          >
            <CartesianGrid
              vertical={false}
              horizontal={true}
              strokeDasharray={"10 10"}
            />
            <XAxis
              axisLine={{ stroke: "#d9d9d9", strokeDasharray: "10 10" }}
              tickLine={false}
              dataKey="name"
              tick={{ fill: "#444", fontSize: 14 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#444", fontSize: 14 }}
              tickFormatter={(tick) => amountFormat(tick)}
            />
            <Tooltip
              formatter={(value) => amountFormat(value)}
              contentStyle={{
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0,12)",
              }}
              labelStyle={{
                color: "#000000",
                fontFamily: "GilroyBold, sans-serif",
                fontSize: 12,
              }}
              itemStyle={{
                color: "#000000",
                fontFamily: "GilroyBold, sans-serif",
                fontSize: 12,
              }}
            />
            <Bar
              dataKey={"inflow"}
              fill="#5C34BA"
              radius={[6, 6, 0, 0]}
              barSize={30}
            >
              <LabelList
                dataKey={"inflow"}
                fill="#000000"
                position={"top"}
                formatter={(tick) => amountFormat(tick)}
                fontSize={10}
              />
            </Bar>
            <Bar
              dataKey={"outflow"}
              fill="#D37444"
              radius={[6, 6, 0, 0]}
              barSize={30}
            >
              <LabelList
                dataKey={"outflow"}
                fill="#000000"
                position={"top"}
                formatter={(tick) => amountFormat(tick)}
                fontSize={10}
              />
            </Bar>
          </ComposedChart>
        )}
      </div>
    </div>
  );
};

export default CustomeChart;
